<template>
    <two-column-layout>
        <card-container>
            <div>
                <h2>{{$t(i18n.title)}}</h2>
                <p>{{$t(i18n.description)}}</p>

                <filterable-drop-down-fragment
                    v-if="!isObjectEmpty(uploadTypes)"
                    :label="$t(i18n.upload_type.label)"
                    :placeholder="$t(i18n.upload_type.placeholder)"
                    v-model="uploadType"
                    :is-invalid="isFieldInvalid($v.uploadType)"
                    :options="uploadTypes"
                    show-info
                    @info-clicked="onInfoClicked('GENERIC_UPLOAD_UPLOAD_TYPE')"
                    @input="loadUploadTypeConstraints"
                >
                    {{ $t(i18n.upload_type.description) }}
                    <template #result>
                        <field-error-messages :validation-state="$v.uploadType" />
                    </template>
                </filterable-drop-down-fragment>

                <div v-if="uploadType && !isObjectEmpty(uploadTypeConstraints)">
                    <fragment-separator/>
                    <h2>{{uploadTypeSpecificTitle}}</h2>
                    <p v-html="uploadTypeSpecificDescription"></p>

                    <upload-fragment
                        :label="$t(i18n.upload_button.label)"
                        @input="handleFileUpload"
                        @file-valid="handleFileValid"
                        @file-invalid="handleFileInvalid"
                        :disabled="finalizing"
                        :supported-mime-types="uploadTypeConstraints.allowedMimeTypes"
                        :file-size-max="uploadTypeConstraints.maxFileSize"
                    >
                        <template #result>
                            <field-error-messages
                                :validation-state="$v.uploadDocument"
                            />
                            <p>{{fileInformationText}}</p>
                        </template>
                    </upload-fragment>

                    <button-group>
                        <button-fragment
                            :disabled="finalizing || uploadDocument == null"
                            @click="submit()"
                        >
                            {{$t(i18n.submission.label)}}
                        </button-fragment>
                    </button-group>
                    <div class="spinner-wrapper" v-if="finalizing">
                        <span class="spinner"></span>
                    </div>
                </div>
            </div>
        </card-container>
    </two-column-layout>
</template>

<script>

    import {Utils} from "@bvm/core";
    import {
        ButtonGroup,
        ButtonFragment,
        CardContainer,
        TwoColumnLayout,
        FragmentSeparator,
        UploadFragment,
        FilterableDropDownFragment
    } from "@bvm/forms";
    import FieldErrorMessages from "../shared/field-error-messages";
    import MediaItemModal from "../shared/media-item-modal";
    import {
        required,
        requiredIf
    } from "vuelidate/lib/validators";
    import {getUploadTypes, uploadDocument} from "./api/generic-upload-api";

    const i18nBuilder = new Utils.I18n.I18nBuilder("generic_upload")
        .add("title")
        .add("description")
        .add("upload_type", b => b.add("label")
            .add("placeholder")
            .add("loading", c => c.add("failure")))
        .add("upload_button", b => b.add("label"))
        .add("INKA_QUOTE", b => b.add("label")
            .add("description"))
        .add("INKA_VALIDITY", b => b.add("label")
            .add("description"))
        .add("file_validation_type")
        .add("file_validation_size")
        .add("file_validation_missing")
        .add("upload_type_fetch_failure")
        .add("submission", b => b.add("label")
            .add("success")
            .add("failure"))

    export const messageKeys = i18nBuilder.getKeys();

    export default {
        name: "generic-upload-form",
        components: {
            ButtonGroup,
            ButtonFragment,
            FragmentSeparator,
            CardContainer,
            TwoColumnLayout,
            UploadFragment,
            FieldErrorMessages,
            FilterableDropDownFragment
        },
        data() {
            return {
                i18n: i18nBuilder.getData(),
                uploadTypes: [],
                uploadTypeProperties: [],
                uploadType: null,
                uploadTypeConstraints: {},
                uploadDocument: null,
                fileValidation: "missing",
                finalizing: false
            }
        },
        computed: {
            uploadTypeSpecificTitle() {
                return this.$t(this.i18n[this.uploadType].label);
            },
            uploadTypeSpecificDescription() {
                return this.$t(this.i18n[this.uploadType].description)
            },
            isUploadDisabled() {
                return false;
            },
            fileInformationText() {
                return this.uploadDocument ? `${this.uploadDocument.name} (${(this.uploadDocument.size / 1024 / 1024).toFixed(2)}MB)` : "";
            }
        },
        methods: {
            isObjectEmpty(obj) {
                return obj && Object.keys(obj).length === 0;
            },
            isFieldInvalid(field) {
                return field.$dirty && field.$invalid;
            },
            onInfoClicked(mediaItemTag) {
                this.$bvmModal.show(MediaItemModal, {
                    itemTag: mediaItemTag
                }, {
                    clickToClose: true
                });
            },
            handleFileUpload(file) {
                this.uploadDocument = file;
            },
            handleFileValid() {
                this.fileValidation = null;
            },
            handleFileInvalid(error) {
                this.$v.uploadDocument.$touch();
                this.fileValidation = error;
                this.uploadDocument = null;
            },
            loadUploadTypeConstraints() {
                this.uploadTypeConstraints = this.uploadTypeProperties.filter(props => props.uploadType === this.uploadType)[0];
            },
            submit() {
                this.$v.$touch();
                if (this.$v.$invalid || this.finalizing) {
                    return;
                }

                this.finalizing = true;

                const formData = new FormData();
                formData.append("uploadType", this.uploadType);
                formData.append("file", this.uploadDocument);

                uploadDocument(formData)
                    .then(() => {
                        Bovemij.notifySuccess(this.$t(this.i18n.submission.success));
                        this.resetState();
                    })
                    .catch(() => Bovemij.notifyFailure('',this.$t(this.i18n.submission.failure)))
                    .finally(() => this.finalizing = false);
            },
            resetState() {
                this.uploadType = null;
                this.uploadDocument = null;
                this.$v.$reset();
            }
        },
        created() {
            getUploadTypes()
                .then(fetchedUploadTypes => {
                    for (const fetchedUploadType of fetchedUploadTypes) {
                        this.uploadTypes.push({value:fetchedUploadType.uploadType, label:fetchedUploadType.label});
                    }
                    this.uploadTypeProperties = fetchedUploadTypes;
                }).catch(() => Bovemij.notifyFailure('',this.$t(this.i18n.upload_type_fetch_failure)))
        },
        validations() {
            return {
                uploadType: { required },
                uploadDocument: { ["generic_upload_" + this.fileValidation]: requiredIf(() => this.fileValidation != null) }
            }
        }
    }
</script>